/**
 * Shared helpers for the per-domain admin API modules.
 *
 * These were previously private members of the AdminService god class in
 * services/auth.ts; the domain modules in this directory share them instead.
 */

// Request deduplication cache: prevents duplicate concurrent API calls from
// React StrictMode double-fires and multiple components mounting
// simultaneously. Entries evict 100ms after the request settles.
const requestCache = new Map<string, Promise<any>>();

export function dedup<T>(key: string, factory: () => Promise<T>): Promise<T> {
  const existing = requestCache.get(key);
  if (existing) return existing as Promise<T>;
  const promise = factory();
  requestCache.set(key, promise);
  // The eviction chain swallows the rejection it would otherwise re-raise:
  // callers handle the error on `promise`, and a second, unobserved copy of
  // it surfaced as an unhandled rejection for every failed request.
  promise
    .finally(() => {
      setTimeout(() => {
        // A newer request under the same key (after a forget) is not ours to evict.
        if (requestCache.get(key) === promise) requestCache.delete(key);
      }, 100);
    })
    .catch(() => undefined);
  return promise;
}

/**
 * Drop every cached read whose key starts with `prefix`. A write calls it so
 * the reload right after it reaches the server: within the 100ms tail above,
 * "disable, list, enable, list" otherwise got the list from before the enable
 * back, and the service still read Disabled.
 */
export function forgetDedup(prefix: string): void {
  for (const key of [...requestCache.keys()]) {
    if (key.startsWith(prefix)) requestCache.delete(key);
  }
}

/** Run a write, then forget the cached reads it makes stale (whether it succeeded or not). */
export async function writeThenForget<T>(prefix: string, write: () => Promise<T>): Promise<T> {
  try {
    return await write();
  } finally {
    forgetDedup(prefix);
  }
}

/**
 * Normalize list responses to an array, preferring common envelope keys.
 * Mirrors the legacy AdminService.extractItems exactly: probes the raw
 * value, then `items`, then endpoint-specific alternate keys, then `data`,
 * and degrades to [] when no shape matches.
 */
export function extractItems<T = any>(json: any, altKeys: string[] = []): T[] {
  if (Array.isArray(json)) return json as T[];
  if (Array.isArray(json?.items)) return json.items as T[];
  for (const k of altKeys) {
    if (Array.isArray(json?.[k])) return json[k] as T[];
  }
  if (Array.isArray(json?.data)) return json.data as T[];
  return [] as T[];
}
